import moment from "moment";
import { v4 as uuidv4 } from 'uuid';

import { 
  MembershipSchema, MessageSchema, GroupedMessageListType, 
  FilterOptionSchema, PaymentStatusType, ResourceType, 
  MyBusinessSchema, TimeBlock, TimeSlot, ResourceSchema, 
  ResourceBookingSchema, IdpAccountRequestSchema, BookingStatusType,
   PlanSchema, ResourcePlanSchema,
   MemberResourceBookingPlanIdsMap, MemberResourceBookingBalanceMap, PeriodType, ResourceOptionType } from '../data/types'
import { ReactComponent as VisaCardIcon } from '../icons/visaCardIcon.svg'
import { ReactComponent as MasterCardIcon } from '../icons/masterCardIcon.svg'
import { ReactComponent as AmexCardIcon } from '../icons/amexCardIcon.svg'

import inventoryCatalog from '../data/inventoryCatalog.json';
import { User } from "firebase/auth";


export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
      return `${minutes} min`;
  } else if (minutes < 1440) { // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  } else if (minutes < 10080) { // Less than 7 days
      const days = Math.floor(minutes / 1440);
      return `${days} ${days === 1 ? 'day' : 'days'}`;
  } else if (minutes < 43200) { // Less than 30 days
      const weeks = Math.floor(minutes / 10080);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  } else { // 30+ days
      const months = Math.floor(minutes / 43200);
      return `${months} ${months === 1 ? 'month' : 'months'}`;
  }
};


export const isFirstBookingWithPlan = (
  username: string,
  member: MembershipSchema | null | undefined,
  resource_id: string,
  new_plan_id: string,
  current_booking_balance?: number | null
) => {
  const current_plan_ids = member && member.booking_plan_ids ? JSON.parse(member.booking_plan_ids) : {};
  const current_plan_id = current_plan_ids[resource_id] || null;
  
  let booking_balance = current_booking_balance;
  if (booking_balance === null || booking_balance === undefined) {
    const current_booking_balances = member && member.booking_balances ? JSON.parse(member.booking_balances) : {};
    booking_balance = current_booking_balances[resource_id] || null;
  }
  
  return current_plan_id === null || current_plan_id === "" || current_plan_id !== new_plan_id || booking_balance === 0;
}

export const extractMemberResourceMapFromJsonString = <T extends MemberResourceBookingPlanIdsMap | MemberResourceBookingBalanceMap>(
  jsonString: string,
  resourceId: string,
  defaultValue: T extends MemberResourceBookingPlanIdsMap ? string : number
): T extends MemberResourceBookingPlanIdsMap ? string : number => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return defaultValue;
    }
    const parsed = JSON.parse(jsonString) as T;
    return (parsed[resourceId] ?? defaultValue) as T extends MemberResourceBookingPlanIdsMap ? string : number;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberResourceMap from JSON:', error);
    }
    return defaultValue;
  }
}

export const convertMemberResourceMapToJsonString = <T extends MemberResourceBookingPlanIdsMap | MemberResourceBookingBalanceMap>(
  memberResourceMap: T,
  resourceId: string,
  value: string | number
): string => {
  try {
    const updatedMap = { ...memberResourceMap, [resourceId]: value };
    return JSON.stringify(updatedMap);
  } catch (error) {
    console.error('Error converting MemberResourceMap to JSON:', error);
    return JSON.stringify({});
  }
}



export const formatMaxHours = (plan: ResourcePlanSchema): string => {

  const maxHours = (["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
    ? Math.floor((plan.plan_max_hours || 0) / 24) 
    : (plan.plan_max_hours || 0)
  return maxHours ? `${maxHours} ${["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any) ? "Days" : "Hours"}` : '';
};


export const extractPlanFields = <T extends PlanSchema | ResourcePlanSchema>(member: MembershipSchema | ResourceBookingSchema): T => {
  if (!member) return {} as T;
  
  return Object.entries(member)
    .filter(([key]) => key in (member as T))
    .reduce((acc, [key, value]) => ({
      ...acc,
      [key]: value
    }), {} as T);
};

export const getPeriodStyles = (periodType: TimeSlot['period_type']) => {
  switch (periodType) {
    case 'STANDARD':
      return {
        bg: 'bg-[#EDF7FF]',
        border: 'border-[#BBD7FF]',
        text: 'text-[#0B5FFF]',
        dot: 'bg-[#0B5FFF]'
      };
    case 'PEAK':
      return {
        bg: 'bg-[#FFF1E6]',
        border: 'border-[#FFC9A6]',
        text: 'text-[#D9480F]',
        dot: 'bg-[#D9480F]'
      };
    case 'OFF_PEAK':
      return {
        bg: 'bg-emerald-50/70',
        border: 'border-emerald-200',
        text: 'text-emerald-400',
        dot: 'bg-emerald-400'
      };
    default:
      return {
        bg: 'bg-[#EDF7FF]',
        border: 'border-[#BBD7FF]',
        text: 'text-[#0B5FFF]',
        dot: 'bg-[#0B5FFF]'
      };
  }
};


export const formatIDPUser = (user: User): IdpAccountRequestSchema => {
  const idpUser: IdpAccountRequestSchema = {
      uid: user.uid,
      emailVerified: user.emailVerified,
      displayName: user.displayName || '',
      isAnonymous: user.isAnonymous,
      photoURL: user.photoURL || '',
      // providerData: user.providerData as IdpProviderData[],
      // stsTokenManager: user.metadata.lastLoginAt || '',
      createdAt: user.metadata.creationTime || '',
      // lastLoginAt: user.metadata.lastLoginAt || '',
      providerId: user?.providerData?.[0]?.providerId || '',
      // apiKey: user.apiKey || '',
      // appName: user.appName || ''
  }

  // console.log("user.providerData", user.providerData)

  if (user.email){
    idpUser.email = user.email;
  }
  // if (user.providerData && user.providerData.length > 0){
  //   // idpUser.providerData = user.providerData as IdpProviderData[];
  // }
  return idpUser;
}


export const buildPrompt = (businessDetails: MyBusinessSchema) => {
  if (!businessDetails || !businessDetails.business_name || !businessDetails.business_category || !businessDetails.business_type || !businessDetails.cooking_state || !businessDetails.cooking_county || !businessDetails.kitchen_type || !businessDetails.business_stage || !businessDetails.max_chefs) {
    return ''
  }
  return `
  As a food business consultant, please provide detailed guidance on licensing and compliance requirements for the following food business:

  Business Details:
  - Name: ${businessDetails.business_name}
  - Category: ${businessDetails.business_category}
  - Type: ${businessDetails.business_type}
  - Location: ${businessDetails.cooking_state}, ${businessDetails.cooking_county}
  - Kitchen Type: ${businessDetails.kitchen_type}
  - Business Stage: ${businessDetails.business_stage}
  - Maximum Chefs: ${businessDetails.max_chefs}

  Please provide:
  1. Required licenses and permits for this specific food business type and location
  2. Health and safety compliance requirements
  3. Food handling and storage regulations
  4. Kitchen facility requirements based on the specified equipment and features
  5. Documentation needed for regulatory compliance
  6. Timeline estimates for obtaining necessary permits
  7. Estimated costs for licenses and compliance measures
  8. Any other relevant information that would be helpful for the business owner to know

  Focus on requirements specific to ${businessDetails.cooking_state} and ${businessDetails.cooking_county} jurisdictions.
  `
}


export const getActionButtonLabel = (resourceType: ResourceType) => {
  var actionButtonLabel = "Book"
  switch(resourceType){
    case 'kitchen':
      actionButtonLabel = 'Book'
      break
    case 'documentation':
      actionButtonLabel = 'Obtain'
      break
    case 'training':
      actionButtonLabel = 'Register'
      break
    case 'funding':
      actionButtonLabel = 'Apply'
      break
    case 'expert':
      actionButtonLabel = 'Schedule'
      break
    case 'farm':
      actionButtonLabel = 'Place Order'
      break
    default:
      actionButtonLabel = 'Book'
      break
  }
  return actionButtonLabel
}


export const calculateAvailableTimeBlocks = (
  openTime: string,
  closeTime: string,
  requestedHours: number,
  bookings: Array<ResourceBookingSchema>,
  baseDate: moment.Moment // NEW: date to anchor the time blocks
): TimeBlock[] => {
  const blocks: TimeBlock[] = [];

  // Parse times
  const startMoment = moment(openTime, "HH:mm A");
  const endMoment = moment(closeTime, "HH:mm A");

  // Validate inputs
  if (!startMoment.isValid() || !endMoment.isValid()) {
    return blocks;
  }

  // Anchor parsed times to the selected day instead of today
  startMoment.year(baseDate.year()).month(baseDate.month()).date(baseDate.date());
  endMoment.year(baseDate.year()).month(baseDate.month()).date(baseDate.date());

  // Handle overnight ranges (e.g., 20:00 - 02:00 next day)
  if (endMoment.isSameOrBefore(startMoment)) {
    endMoment.add(1, "day");
  }

  let currentTime = startMoment.clone();
  let iterations = 0;
  const maxIterations = 1000; // Prevent infinite loops

  while (
    currentTime.clone().add(requestedHours, "hours").isSameOrBefore(endMoment) &&
    iterations < maxIterations
  ) {
    const blockEnd = currentTime.clone().add(requestedHours, "hours");
    let isAvailable = true;

    for (const booking of bookings) {
      const bookingStart = moment(booking.start_time);
      const bookingEnd = moment(booking.end_time);

      if (currentTime.isBefore(bookingEnd) && blockEnd.isAfter(bookingStart)) {
        isAvailable = false;
        break;
      }
    }

    if (isAvailable) {
      blocks.push({
        start: currentTime.clone(),
        end: blockEnd.clone(),
      });
    }

    currentTime.add(30, "minutes");
    iterations++;
  }

  return blocks;
};


export const calculateAvailableTimeBlocksForSlots = (
  timeSlots: TimeSlot[],
  requestedHours: number,
  bookings: Array<ResourceBookingSchema>,
  baseDate: moment.Moment // NEW: date to anchor
): TimeBlock[] => {
  if (!timeSlots || timeSlots.length === 0) return [];

  const allBlocks: TimeBlock[] = [];
  timeSlots.forEach((slot) => {
    if (slot && slot.open_time && slot.close_time) {
      const blocks = calculateAvailableTimeBlocks(
        slot.open_time,
        slot.close_time,
        requestedHours,
        bookings,
        baseDate
      );
      allBlocks.push(...blocks);
    }
  });

  // Sort blocks by start time to keep chronological order
  allBlocks.sort((a, b) => a.start.diff(b.start));
  return allBlocks;
};


export const areDatesInSameMonth = (date1: Date, date2: Date) => {
  // Extract year and month components from both dates
  const year1 = date1.getFullYear();
  const month1 = date1.getMonth();

  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();

  // Compare the year and month components
  return year1 === year2 && month1 === month2;
};


export const findBookingsForDay = (day: moment.Moment, bookings: ResourceBookingSchema[]): Array<ResourceBookingSchema> => {
  const bookingsForDay: Array<ResourceBookingSchema> = [];
  if (bookings && bookings.length > 0) {
    for (const booking of bookings) {
      const startTime = moment(booking.start_time);
      const endTime = moment(booking.end_time);

      if (
        startTime.isSameOrBefore(day, "day") &&
        endTime.isSameOrAfter(day, "day")
      ) {
        bookingsForDay.push(booking);
      }
    }
  }
  return bookingsForDay;
};


export const areBookingsLessThan12Hours = (day: Date, bookings: ResourceBookingSchema[]) => {
  // const startOfDay = moment(day).startOf("day");
  // const endOfDay = moment(day).endOf("day");
  let totalTime = 0; 
  const bookingsInDay = findBookingsForDay(moment(day), bookings);
  for (const booking of bookingsInDay) {
    totalTime += moment(booking.end_time).diff(booking.start_time);
  }
  const test = totalTime < 12 * 60 * 60 * 1000;

  return test;
};


export const isTimeSlot = (value: TimeSlot): boolean => {
  return !!(
    value && typeof value === 'object' &&
    'open_time' in value && value.open_time &&
    'close_time' in value && value.close_time
  );
};


export const dayAvailability = (
  day: Date,
  currentResource: ResourceSchema,
  nHours: number,
  bookings: ResourceBookingSchema[]
) => {
  const dayOfWeek = moment(day).format("ddd").toLowerCase();
  const dayOptions = currentResource?.resource_options?.[dayOfWeek] as TimeSlot[];
  if (!dayOptions || !(Array.isArray(dayOptions)) || dayOptions.length === 0) {
    return false;
  }

  const availableBlocks = calculateAvailableTimeBlocksForSlots(
    dayOptions,
    nHours,
    findBookingsForDay(moment(day), bookings || []),
    moment(day)
  );
  return availableBlocks.length > 0;
};

export const formatDateString = (monthString?: number, yearString?: number): string => {
  if (monthString && yearString && yearString.toString().length === 4){
    return `${(monthString.toString().length < 2) ? `0${monthString}` : monthString}/${yearString.toString().slice(-2)}`
  } else if (yearString && yearString.toString().length === 4) {
    return yearString.toString().slice(-2);
  } else if (monthString && monthString.toString().length === 1){
    return `0${monthString}`
  }
  return ''
}


export const filterItemsByCategoryAndName = (data: FilterOptionSchema[], category: string, itemName: string): string[] => {
  const categoryData = data.find(cat => cat.category.toLowerCase() === category.toLowerCase());
  if (!categoryData) {
    return [];
  }
  return categoryData.items.filter(item => item.toLowerCase().includes(itemName.toLowerCase()));
}

export const searchInventoryCatalog = (category: string, query: string): string[] => {
  const inventoryItems: FilterOptionSchema[] = inventoryCatalog as FilterOptionSchema[];
  return filterItemsByCategoryAndName(inventoryItems, category, query)
}

export const getItemsByCategory = (data: FilterOptionSchema[], category: string): string[] => {
  const categoryData = data.find(cat => cat.category.toLowerCase() === category.toLowerCase());
  if (!categoryData) {
    return [];
  }
  return categoryData.items
}

export const listInventoriesByCategory = (category: string): string[] => {
  const inventoryItems: FilterOptionSchema[] = inventoryCatalog as FilterOptionSchema[];
  return getItemsByCategory(inventoryItems, category)
}

export const requirementCodes: string[] = [
  "FMP",
  "MFRST",
  "CLNRY",
  "PSTRT",
  "SSA",
  "SSALRGNS",
  "SSFHNDLR",
  "SSPRC",
  "SSI",
  "PIC Ohio",
  "SSWP",
  "SSMGR",
  "SMMR",
  "TXFG",
  "ID",
  "PID",
  "GLI",
  "HACCP",
];


// export const fetchRequirementsMet = (communityRequirements: RequirementSchema[], memberCredentials: CredentialSchema[], paymentMethods: any[]): CurrentRequirementsSchema[] => {

//   console.log("communityRequirements", communityRequirements)
//   console.log("memberCredentials", memberCredentials)
//   console.log("paymentMethods", paymentMethods)
//   return [
//       {
//         code: communityRequirements[0].code,
//         name: communityRequirements[0].name,
//         description: communityRequirements[0].description || "",
//         isMet: memberCredentials.some((item) => (item["category"] === communityRequirements[0].name && item['is_valid'])),
//         isRequired: false
//       }
//   ]
// }


// export const fetchRequirementsLookUp = (memberCredentials: CredentialSchema[], paymentMethods: any[]): CurrentRequirementsSchema[] => {
//   return [
//       {
//         code: "FMP",
//         name: "Foodservice Management Professional Certification",
//         description: "",
//         is_met: memberCredentials.some((item) => (item["category"] === "FMP" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "MFRST", 
//         name: "ManageFirst", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "MFRST" && item['is_valid'])) ,
//         is_required: false
//       },
//       {
//         code: "CLNRY",
//         name: "National Restaurant Association Culinary Exams",
//         description: "",
//         is_met: memberCredentials.some((item) => (item["category"] === "CLNRY" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "PSTRT", 
//         name: "ProStart",  
//         description: "",
//         is_met: memberCredentials.some((item) => (item["category"] === "PSTRT" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SSA", 
//         name: "ServSafe Alcohol", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSA" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSALRGNS",
//         name: "ServSafe Allergens Online Course", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSALRGNS" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SSFHNDLR", 
//         name: "ServSafe Food Handler", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSFHNDLR" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSPRC",
//         name: "ServSafe Instructor and Proctor Certificates", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSPRC" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSI",
//         name: "ServSafe International Food Safety", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSI" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "PIC Ohio",
//         name: "ServSafe Person in Charge – Ohio",
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "PIC Ohio" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SSWP", 
//         name: "ServSafe Workplace", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSWP" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSMGR",
//         name: "ServSafe: Food Protection Manager Certification", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSMGR" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SMMR", 
//         name: "Summer Institutes", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SMMR" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "TXFG", 
//         name: "Texas FoodGuard", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "TXFG" && item['is_valid'])), 
//         is_required: false
//       },
//       {
//         code: "GLI", 
//         name: "General Liability Insurance", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "GLI" && item['is_valid'])), 
//         is_required: false
//       },
//       { 
//         code: "HACCP", 
//         name: "HACCP Certificate", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "HACCP" && item['is_valid'])), 
//         is_required: false
//       },
//       { 
//         code: "ID", 
//         name: "Government ID", 
//         description: "Upload a government ID with your photo to verify your identity.", 
//         is_met: memberCredentials.some((item) => (item["category"] === "ID" && item['is_valid'])), 
//         is_required: false  
//       },
//       { 
//         code: "PID", 
//         name: "Photo ID", 
//         description: "Upload a legal document with your photo to verify your identity.", 
//         is_met: memberCredentials.some((item) => (item["category"] === "PID" && item['is_valid'])),
//         is_required: true
//       },
//       { 
//         code: "PAY",
//         name: "Add your payment method", 
//         description: "Add your preferred payment method for easy transactions.", 
//         is_met: (paymentMethods && paymentMethods.length > 0) ? true : false, 
//         is_required: true
//       }
//   ]
// }

// export const getDirectLinkFromGoogleDriveSharedLink = (sharedLink: string | undefined): string => {
//   // Regular expression to match Google Drive shared links
//   const regex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view\?usp=sharing/i;
//   if (sharedLink){
//     const match = regex.exec(sharedLink);
//     if (!match) {
//       return ""; // Not a valid Google Drive shared link
//     }
//     const fileId = match[1]; // Extract the file ID from the match
//     // console.log("fileId", fileId)
//     // Potential direct link format (replace with your actual generation logic)
//     // You might need to use a Google Drive API or a third-party service to obtain the actual direct link based on the file ID.
//     // https://drive.usercontent.google.com/download?id=1EqddZcRTk9btkxAhWu3Y7osd0KrFdsYk&export=view&authuser=0
//     // const potentialDirectLink = `https://drive.google.com/uc?export=view&id=${fileId}`;
//     // https://drive.usercontent.google.com/download?id=1EqddZcRTk9btkxAhWu3Y7osd0KrFdsYk&export=view&authuser=0
//     // https://drive.usercontent.google.com/download?id=1EqddZcRTk9btkxAhWu3Y7osd0KrFdsYk&export=view
//     const potentialDirectLink = `https://drive.usercontent.google.com/download?id=${fileId}&export=view`;
  
//     return potentialDirectLink;
//   }
//   return ""
// }

export const handleGroupMessages = (allMessages: MessageSchema[], groupByField: string): GroupedMessageListType => {
  const groupMessages = allMessages.reduce((result: GroupedMessageListType, message: MessageSchema) => {
      const groupByValue = message[groupByField as keyof MessageSchema] as string;
      if (!result[groupByValue]) {
          result[groupByValue] = [];
      }
      result[groupByValue].push(message);
      return result;
  }, {});
  return groupMessages
}

export const getDirectLinkFromGoogleDriveSharedLink = (link: string | undefined): string => {
  // Regular expressions to match S3 and Google Drive shared links
  // const s3Regex = /^https:\/w\/s3\.amazonaws\.com\/.+\/.+$/;
  const googleDriveRegex = /^https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view\?usp=sharing$/;
  // console.log("link", link)
  if (link && link.includes('s3')) {
    // If it matches the S3 regex, return the link as is
    // console.log("s3 link", link)
    return link;
  } else if (link && googleDriveRegex.test(link)) {
    // If it matches the Google Drive regex, convert to direct link format
    const match = link.match(googleDriveRegex);
    if (match) {
      const fileId = match[1];
      return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
  }

  

  // If neither regex matches, return an empty string
  return '';
}

const getDayName = (dateString: string): string => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day name
  return date.format('dddd');
}


const getDayOfMonth = (dateString: string): number  => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day of the month
  return date.date();
}


const getDayOfMonthWithSuffix = (dateString: string): string => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day of the month with ordinal suffix
  return date.format('Do');
}


const addDaysToToday = (days: number): string => {
  // Get today's date and add the specified number of days
  const newDate = moment().add(days, 'days');
  // Format the new date as YYYY-MM-DD
  return newDate.format('YYYY-MM-DD');
}

const calculateDateDifference = (fromDate: string, toDate: string, booking_recurrence: string) => {
  var nOccurrences = 0
  if (fromDate && toDate){
    // Parse the dates
    const start = moment(fromDate, 'YYYY-MM-DD');
    const end = moment(toDate, 'YYYY-MM-DD');
    switch(booking_recurrence){
      case 'daily':
        nOccurrences = end.diff(start, 'days') + 1;
        return nOccurrences
      case 'weekly':
        nOccurrences = end.diff(start, 'weeks');
        let startDay = getDayOfMonth(fromDate)
        let endDay = getDayOfMonth(toDate)
        if (endDay >= startDay){
          nOccurrences += 1
        }
        return nOccurrences
      case 'monthly':
        nOccurrences = end.diff(start, 'months') + 1;
        return nOccurrences
      default:
        return 1
    }
  }
  return nOccurrences
}


const maxHours = (capacity: number, recurrence: string | 'daily' | 'weekly' | 'monthly' | 'annual',  hours_left: number) => {
  if (recurrence && capacity){
    switch (recurrence){
      case 'daily':
        // 24 hrs
        hours_left = hours_left ? hours_left : Math.round(16 / capacity)
        return hours_left
      case 'weekly':
        // 168 hrs
        hours_left = hours_left ? hours_left : Math.round(115 / capacity)
        return hours_left
      case 'monthly':
        // 730 hrs
        hours_left = hours_left ? hours_left : Math.round(500 / capacity)
        return hours_left
      case 'yearly':
        // 8760 hrs
        hours_left = hours_left ? hours_left : Math.round(6000 / capacity)
        return hours_left
      default:
        return 0
    }
  }
  return 0
}


const objectToFormData = (obj: { [key: string]: any }): FormData =>  {
  const formData = new FormData();
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      formData.append(key, value);
    }
  }
  return formData;
}

 // Define a function to find member by attribute value
 const findMemberByAttribute = (objects: MembershipSchema[], attribute: keyof MembershipSchema, value: any) => {
  for (const obj of objects) {
      if (obj[attribute] === value) {
          return obj;
      }
  }
  return {};
}


 // Define a function to find an object by attribute value
 export const isResourceSaved = <T extends { [key: string]: any }>(resourceId: string, objects: T[]) => {
  for (const obj of objects) {
      if (!obj) continue;
      if (obj['resource_id'] === resourceId || obj['id'] === resourceId) {
        return true;
      }
  }
  return false;
}


 // Define a function to find an object by attribute value
 const findObjectByAttribute = <T>(objects: T[], attribute: keyof T, value: string) => {
  for (const obj of objects) {
      if (obj[attribute] === value) {
          return obj;
      }
  }
  return {};
}


export const planRecurrenceMap = {
  'hourly': 'hour',
  'daily': 'day',
  'weekly': 'week',
  'monthly': 'month',
  'annual': 'year'
}


const getMembershipStatusColor = (status: string): {bgColor: string, primaryTextColor: string, secondaryTextColor: string} => {
  if (status) {
    status = status.toLowerCase();
    switch (status) {
      case "approved":
        return {bgColor: "#E7F5EC", primaryTextColor: '#b9dc8d', secondaryTextColor: '#819a62'};
      case "active":
        return {bgColor: "#ebf5ec", primaryTextColor: '#399f48', secondaryTextColor: '#afd8b5'};
      case "verified":
        return {bgColor: "#fef5ea", primaryTextColor: '#F69E35', secondaryTextColor: '#fce1c2'};
      case "pending":
        return {bgColor: "#edf5f7", primaryTextColor: '#51a0b5', secondaryTextColor: '#a8cfda'};
      case "declined":
        return {bgColor: "#faeeee", primaryTextColor: '#CE5D5D', secondaryTextColor: '#e6aeae'};
      case "inactive":
        return {bgColor: "#eee", primaryTextColor: '#9a9a9a', secondaryTextColor: '#d6d6d6'};
      case "removed":
        return {bgColor: "#eee", primaryTextColor: '#668790', secondaryTextColor: '#696969'};
      default:
        return {bgColor: "#eee", primaryTextColor: '#bbb', secondaryTextColor: '#696969'};
    }
  }
  return {bgColor: "#eee", primaryTextColor: '#bbb', secondaryTextColor: '#696969'};
};

// pending, declined, approved, verified, active, inactive, removed


const getBookingStatusColor = (status: BookingStatusType) => {
  if (status) {
    // status = status.toLowerCase();
    switch (status) {
      case "pending":
        return {primaryTextColor: "#F69E35", bgColor: '#fef5ea', secondaryTextColor: '#fce1c2'};
      case "approved":
        return {primaryTextColor: "#399f48", bgColor: '#ebf5ec', secondaryTextColor: '#afd8b5'};
      case "in_progress":
        return {primaryTextColor: "#51a0b5", bgColor: '#edf5f7', secondaryTextColor: '#a8cfda'};
      case "canceled":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae'};
      case "completed":
        return {primaryTextColor: "#013847", bgColor: '#e5ebec', secondaryTextColor: '#51a0b5'};
      default:
        return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969'};
    }
  }
  return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969'};
};



export const getPaymentStatusColor = (status: PaymentStatusType) => {
  if (status) {
    switch (status) {
      case "requires_confirmation":
        return {primaryTextColor: "#F69E35", bgColor: '#fef5ea', secondaryTextColor: '#fce1c2', displayText: "Pending"};
      case "succeeded":
        return {primaryTextColor: "#399f48", bgColor: '#ebf5ec', secondaryTextColor: '#afd8b5', displayText: "Succeeded"};
      case "failed":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae', displayText: "Failed"};
      case "canceled":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae', displayText: "Canceled"};
      default:
        return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969', displayText: "Unknown"};
    }
  }
  return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969', displayText: "Unknown"};
};


const lightenColor = (hex: string, percent: number): string => {
  const color = parseInt(hex.replace(/^#/, ''), 16);
  const R = ((color >> 16) + percent);
  const G = (((color >> 8) & 0x00FF) + percent);
  const B = ((color & 0x0000FF) + percent);
  const newColor = ((R << 16) | (G << 8) | B).toString(16);
  return `#${newColor.padStart(6, '0')}`;
}


const darkenColor = (hex: string, percent: number): string => {
  const color = parseInt(hex.replace(/^#/, ''), 16);
  const R = ((color >> 16) - percent);
  const G = (((color >> 8) & 0x00FF) - percent);
  const B = ((color & 0x0000FF) - percent);
  const newColor = ((R << 16) | (G << 8) | B).toString(16);
  return `#${newColor.padStart(6, '0')}`;
}


const servSafeLookUp: {
  requirementCode: string;
  requirementName: string;
}[] = [
  {
    requirementCode: "FMP",
    requirementName: "Foodservice Management Professional Certification",
  },
  { requirementCode: "MFRST", requirementName: "ManageFirst" },
  {
    requirementCode: "CLNRY",
    requirementName: "National Restaurant Association Culinary Exams",
  },
  { requirementCode: "PSTRT", requirementName: "ProStart" },
  { requirementCode: "SSA", requirementName: "ServSafe Alcohol" },
  {
    requirementCode: "SSALRGNS",
    requirementName: "ServSafe Allergens Online Course",
  },
  { requirementCode: "SSFHNDLR", requirementName: "ServSafe Food Handler" },
  {
    requirementCode: "SSPRC",
    requirementName: "ServSafe Instructor and Proctor Certificates",
  },
  {
    requirementCode: "SSI",
    requirementName: "ServSafe International Food Safety",
  },
  {
    requirementCode: "PIC Ohio",
    requirementName: "ServSafe Person in Charge – Ohio",
  },
  { requirementCode: "SSWP", requirementName: "ServSafe Workplace" },
  {
    requirementCode: "SSMGR",
    requirementName: "ServSafe: Food Protection Manager Certification",
  },
  { requirementCode: "SMMR", requirementName: "Summer Institutes" },
  { requirementCode: "TXFG", requirementName: "Texas FoodGuard" },
  { requirementCode: "ID", requirementName: "Government ID" },
  { requirementCode: "PID", requirementName: "Photo ID" },
  { requirementCode: "GLI", requirementName: "General Liability Insurance" },
  { requirementCode: "HACCP", requirementName: "HACCP Certificate" },
];


type AnyObject = Record<string, any>; // A type for any object with string keys

const getChangedAttributes = <T extends AnyObject>(
  originalObj: T,
  newObj: T
): T => {
  const changedAttributes: T = {} as T;
  for (const key in newObj) {
    if (newObj.hasOwnProperty(key)) {
      if (!originalObj.hasOwnProperty(key) || newObj[key] !== originalObj[key]) {
        changedAttributes[key] = newObj[key];
      }
    }
  }
  return changedAttributes;
}

const styleThousandCommas = (number: string | number) => {
  if (typeof number === 'number'){
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  } else if (typeof number === 'string'){
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }
  return number
}

export const stylePhoneNumber = (phone_number: string) => {
  var cleaned = ('' + phone_number).replace(/\D/g, '')
  var match = cleaned.match(/^(0-1|)?(\d{3})(\d{3})(\d{4})$/)
  // let number;
  if (match) {
      var intlCode = (match[1] ? '+1 ' : '')

      if (phone_number.length === 10){
          phone_number = [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
      } else if (phone_number.length > 3 && phone_number.length < 7){
          phone_number = [intlCode, '(', match[2], ') ', match[3]].join('');
      } else if (phone_number.length < 4){
          phone_number = [intlCode, '(', match[2], ')'].join('');
      }

    }
  return phone_number
}

/** Format 9-digit SSN as 111-22-3333; accepts digits only or mixed input. */
export const styleSSN = (ssn: string): string => {
  const digits = ('' + (ssn || '')).replace(/\D/g, '').slice(0, 9)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

const getCreditCardIcon = (paymentMethod: string) => {
  switch (paymentMethod) {
    case 'visa':
      return VisaCardIcon
    case 'mastercard':
      return MasterCardIcon
    case 'amex':
      return AmexCardIcon
    // Add cases for other card types
    default:
      return null
  }
};


export const capitalizeString = (string: string) => string?.replace(/\w/, (c) => c?.toUpperCase());

export const capitalizeWords = (str: string): string => {
  return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export const getUIDFromAccessToken = (token: string | null) => {
  try {
    if (token && token.split('.').length === 3) {
      const user = JSON.parse(atob(token.split('.')[1]));
      // console.log("getUIDFromAccessToken user", user)
      return (user && user.user_id) ? user.user_id : (user && user.uid) ? user.uid : '';
    }
  } catch (e) {
    // Invalid token format
  }
  return '';
};

export const getLastPagePathFromAccessToken = (token: string | null) => {
  try {
    if (token && token.split('.').length === 3) {
      const user = JSON.parse(atob(token.split('.')[1]));
      // console.log("getLastPagePathFromAccessToken user", user)
      return (user && user.last_page_path) ? user.last_page_path : '';
    }
  } catch (e) {
    // Invalid token format
  }
  return '';
};

export const getEmailFromAccessToken = (token: string | null) => {
  try {
    if (token && token.split('.').length === 3) {
      const user = JSON.parse(atob(token.split('.')[1]));
      return user && user.email ? user.email : '';
    }
  } catch (e) {
    // Invalid token format
  }
  return '';
};

export const generateUUID = (): string => {
  return uuidv4();
}

export {
  servSafeLookUp,
  addDaysToToday,
  getDayName,
  getDayOfMonth,
  getDayOfMonthWithSuffix,
  calculateDateDifference,
  maxHours,
  // capitalizeString,
  getCreditCardIcon,
  findObjectByAttribute,
  findMemberByAttribute,
  objectToFormData,
  lightenColor,
  darkenColor,
  styleThousandCommas,
  getBookingStatusColor,
  getMembershipStatusColor,
  getChangedAttributes
}



/**
 * Generates a consistent color based on a username or email string
 * @param identifier - username or email string
 * @returns hex color string
 */
export const generateUserColor = (identifier: string): string => {
  if (!identifier) return '#E6E6E6';
  
  // Create a simple hash from the string
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    const char = identifier.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use the hash to generate consistent colors
  // Generate hue between 0-360, saturation between 40-80%, lightness between 60-80%
  const hue = Math.abs(hash) % 360;
  const saturation = 40 + (Math.abs(hash) % 40); // 40-80%
  const lightness = 60 + (Math.abs(hash) % 20); // 60-80%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Extracts initials from first and last name
 * @param firstName - first name
 * @param lastName - last name
 * @param username - fallback to username if names not available
 * @returns 1-2 character uppercase string
 */
export const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  } else if (firstName) {
    return firstName.charAt(0).toUpperCase();
  } else if (lastName) {
    return lastName.charAt(0).toUpperCase();
  } else if (username) {
    // Extract from email or username
    const cleanUsername = username.split('@')[0]; // Remove domain if email
    return cleanUsername.charAt(0).toUpperCase();
  }
  return '?';
};

/**
 * Determines the time period type (STANDARD, PEAK, or OFF_PEAK) for a given time
 * based on the resource's time slots configuration
 */
export const getTimePeriodType = (
  time: moment.Moment,
  resourceOptions?: ResourceOptionType
): PeriodType => {
  if (!resourceOptions) return 'STANDARD';
  
  const dayOfWeek = time.format('ddd').toLowerCase() as keyof ResourceOptionType;
  const timeSlots = resourceOptions[dayOfWeek] as TimeSlot[] | undefined;
  
  if (!timeSlots || !Array.isArray(timeSlots)) return 'STANDARD';
  
  const timeString = time.format('HH:mm');
  
  // Find the time slot that contains this time
  for (const slot of timeSlots) {
    if (!slot.open_time || !slot.close_time) continue;

    // Build moments for comparison.
    const slotStart = moment(slot.open_time, 'HH:mm A');
    const slotEndRaw = moment(slot.close_time, 'HH:mm A');
    // Handle overnight range (e.g., 20:00 - 02:00)
    const slotEnd = slotEndRaw.isSameOrAfter(slotStart) ? slotEndRaw : slotEndRaw.add(1, 'day');

    const checkTime = moment(timeString, 'HH:mm');
    // Convert checkTime to same format as slot times for comparison
    const checkTimeFormatted = moment(checkTime.format('hh:mm A'), 'hh:mm A');
    // If slotEnd is next day, advance checkTime when needed
    const inRange = (checkTimeFormatted.isSameOrAfter(slotStart) && checkTimeFormatted.isBefore(slotEnd)) ||
                    (checkTimeFormatted.clone().add(1, 'day').isBefore(slotEnd) && checkTimeFormatted.isBefore(slotStart));

    if (inRange) {
      return (slot.period_type || 'STANDARD').toUpperCase() as PeriodType;
    }
  }
  
  return 'STANDARD';
};

/**
 * Determines the time period type for a time block by checking which time slot it falls into
 * Returns the period type of the time slot that contains the start time
 */
export const getTimeBlockPeriodType = (
  startTime: moment.Moment,
  endTime: moment.Moment,
  resourceOptions?: ResourceOptionType
): PeriodType => {
  if (!resourceOptions) return 'STANDARD';
  
  const dayOfWeek = startTime.format('ddd').toLowerCase() as keyof ResourceOptionType;
  const timeSlots = resourceOptions[dayOfWeek] as TimeSlot[] | undefined;
  
  if (!timeSlots || !Array.isArray(timeSlots)) return 'STANDARD';
  
  const startTimeString = startTime.format('HH:mm');
  
  // Find the time slot that contains the start time
  for (const slot of timeSlots) {
    if (!slot.open_time || !slot.close_time) continue;

    const slotStart = moment(slot.open_time, ['HH:mm', 'hh:mm A']);
    const slotEndRaw = moment(slot.close_time, ['HH:mm', 'hh:mm A']);
    const slotEnd = slotEndRaw.isSameOrAfter(slotStart) ? slotEndRaw : slotEndRaw.add(1, 'day');

    const checkTime = moment(startTimeString, 'HH:mm');
    const inRange = (checkTime.isSameOrAfter(slotStart) && checkTime.isBefore(slotEnd)) ||
                    (checkTime.clone().add(1, 'day').isBefore(slotEnd) && checkTime.isBefore(slotStart));

    if (inRange) {
      return (slot.period_type || 'STANDARD').toUpperCase() as PeriodType;
    }
  }
  
  return 'STANDARD';
};

/**
 * Calculates the appropriate rate for a time period
 */
export const getTimePeriodRate = (
  plan: ResourcePlanSchema | undefined,
  periodType: PeriodType
): number => {
  if (!plan) return 0;
  
  switch (periodType) {
    case 'PEAK':
      return plan.peak_rate || plan.plan_rate || 0;
    case 'OFF_PEAK':
      return plan.off_peak_rate || plan.plan_rate || 0;
    case 'STANDARD':
    default:
      return plan.plan_rate || 0;
  }
};

/**
 * Calculates the total cost for a time block with mixed time periods
 * This function splits the booking across different time periods and calculates
 * the cost for each period separately
 */
export const calculateMixedTimeBlockCost = (
  timeBlock: TimeBlock,
  plan: ResourcePlanSchema | undefined,
  resourceOptions?: ResourceOptionType
): { totalCost: number; breakdown: Array<{ period: string; hours: number; rate: number; cost: number }> } => {
  // console.log('calculateMixedTimeBlockCost - plan:', plan);
  // console.log('calculateMixedTimeBlockCost - timeBlock:', timeBlock);
  // console.log('calculateMixedTimeBlockCost - resourceOptions:', resourceOptions);
  
  if (!plan || !timeBlock) {
    // console.log('calculateMixedTimeBlockCost - Early return due to missing params');
    return { totalCost: 0, breakdown: [] };
  }
  
  const startTime = moment(timeBlock.start);
  const endTime = moment(timeBlock.end);
  
  // console.log('calculateMixedTimeBlockCost - startTime:', startTime.format('HH:mm'));
  // console.log('calculateMixedTimeBlockCost - endTime:', endTime.format('HH:mm'));
  
  // If no resource options provided, use fallback calculation
  if (!resourceOptions) {
    // console.log('calculateMixedTimeBlockCost - Using fallback calculation');
    // Fallback to simple calculation
    const duration = endTime.diff(startTime, 'hours', true);
    const periodType = getTimePeriodType(startTime, resourceOptions);
    const rate = getTimePeriodRate(plan, periodType);
    // console.log('calculateMixedTimeBlockCost - duration:', duration);
    // console.log('calculateMixedTimeBlockCost - periodType:', periodType);
    // console.log('calculateMixedTimeBlockCost - rate:', rate);
    // console.log('calculateMixedTimeBlockCost - totalCost:', rate * duration);
    return {
      totalCost: rate * duration,
      breakdown: [{ period: periodType, hours: duration, rate, cost: rate * duration }]
    };
  }
  
  const dayOfWeek = startTime.format('ddd').toLowerCase() as keyof ResourceOptionType;
  const timeSlots = resourceOptions[dayOfWeek] as TimeSlot[] | undefined;
  
  // console.log('calculateMixedTimeBlockCost - dayOfWeek:', dayOfWeek);
  // console.log('calculateMixedTimeBlockCost - timeSlots:', timeSlots);
  
  if (!timeSlots || !Array.isArray(timeSlots)) {
    // console.log('calculateMixedTimeBlockCost - Using fallback calculation');
    // Fallback to simple calculation
    const duration = endTime.diff(startTime, 'hours', true);
    const periodType = getTimePeriodType(startTime, resourceOptions);
    const rate = getTimePeriodRate(plan, periodType);
    // console.log('calculateMixedTimeBlockCost - duration:', duration);
    // console.log('calculateMixedTimeBlockCost - periodType:', periodType);
    // console.log('calculateMixedTimeBlockCost - rate:', rate);
    // console.log('calculateMixedTimeBlockCost - totalCost:', rate * duration);
    return {
      totalCost: rate * duration,
      breakdown: [{ period: periodType, hours: duration, rate, cost: rate * duration }]
    };
  }
  
  const breakdown: Array<{ period: string; hours: number; rate: number; cost: number }> = [];
  let totalCost = 0;
  
  // Sort time slots by open_time
  const sortedSlots = [...timeSlots].sort((a, b) => (a.open_time || '').localeCompare(b.open_time || ''));
  
  // console.log('calculateMixedTimeBlockCost - sortedSlots:', sortedSlots);
  
  let currentTime = startTime.clone();
  
  // console.log('calculateMixedTimeBlockCost - Starting while loop');
  // console.log('calculateMixedTimeBlockCost - currentTime:', currentTime.format('HH:mm'));
  // console.log('calculateMixedTimeBlockCost - endTime:', endTime.format('HH:mm'));
  // console.log('calculateMixedTimeBlockCost - currentTime.isBefore(endTime):', currentTime.isBefore(endTime));
  
  while (currentTime.isBefore(endTime)) {
    // console.log('calculateMixedTimeBlockCost - Inside while loop');
    // console.log('calculateMixedTimeBlockCost - currentTime:', currentTime.format('HH:mm'));
    
    // Extract currentTime value to avoid unsafe reference in loop
    const currentTimeString = currentTime.format('hh:mm A');
    
    // Find the current time slot by checking if current time falls within any slot
    const currentSlot = sortedSlots.find(slot => {
      if (!slot.open_time || !slot.close_time) return false;
      
      // Parse slot times to moments for comparison
      const slotStart = moment(slot.open_time, 'HH:mm A');
      const slotEndRaw = moment(slot.close_time, 'HH:mm A');
      const slotEnd = slotEndRaw.isSameOrAfter(slotStart) ? slotEndRaw : slotEndRaw.add(1, 'day');
      
      // Check if current time falls within this slot
      const currentTimeFormatted = moment(currentTimeString, 'hh:mm A');
      return currentTimeFormatted.isSameOrAfter(slotStart) && currentTimeFormatted.isBefore(slotEnd);
    });
    
    // console.log('calculateMixedTimeBlockCost - currentSlot:', currentSlot);
    
    if (!currentSlot) {
      // console.log('calculateMixedTimeBlockCost - No matching slot, using standard rate');
      // No matching slot, use standard rate
      const remainingHours = endTime.diff(currentTime, 'hours', true);
      const rate = plan.plan_rate || 0;
      const cost = rate * remainingHours;
      // console.log('calculateMixedTimeBlockCost - remainingHours:', remainingHours);
      // console.log('calculateMixedTimeBlockCost - rate:', rate);
      // console.log('calculateMixedTimeBlockCost - cost:', cost);
      breakdown.push({ period: 'STANDARD', hours: remainingHours, rate, cost });
      totalCost += cost;
      break;
    }
    
    // Calculate the end time for this slot (either end of slot or end of booking)
    // Anchor slot times to the same date as the time block
    const slotStartTime = moment(currentSlot.open_time, 'HH:mm A');
    const slotEndTime = moment(currentSlot.close_time, 'HH:mm A');
    
    // Set the date to match the time block's date
    slotStartTime.year(startTime.year()).month(startTime.month()).date(startTime.date());
    slotEndTime.year(startTime.year()).month(startTime.month()).date(startTime.date());
    
    const slotEnd = slotEndTime.isSameOrAfter(slotStartTime) ? slotEndTime : slotEndTime.add(1, 'day');
    
    // Use the earlier of slot end or booking end
    const actualEndTime = slotEnd.isBefore(endTime) ? slotEnd : endTime;
    
    // console.log('calculateMixedTimeBlockCost - slotEnd:', slotEnd.format('HH:mm'));
    // console.log('calculateMixedTimeBlockCost - endTime:', endTime.format('HH:mm'));
    // console.log('calculateMixedTimeBlockCost - slotEnd.toDate():', slotEnd.toDate());
    // console.log('calculateMixedTimeBlockCost - endTime.toDate():', endTime.toDate());
    // console.log('calculateMixedTimeBlockCost - slotEnd.isBefore(endTime):', slotEnd.isBefore(endTime));
    
    // If the slot end is before the booking end, we need to continue to the next period
    // This ensures we handle transitions between periods correctly
    
    const hoursInSlot = actualEndTime.diff(currentTime, 'hours', true);
    const periodType = (currentSlot.period_type || 'STANDARD').toUpperCase() as PeriodType;
    const rate = getTimePeriodRate(plan, periodType);
    const cost = rate * hoursInSlot;
    
    // console.log('calculateMixedTimeBlockCost - hoursInSlot:', hoursInSlot);
    // console.log('calculateMixedTimeBlockCost - periodType:', periodType);
    // console.log('calculateMixedTimeBlockCost - rate:', rate);
    // console.log('calculateMixedTimeBlockCost - cost:', cost);
    // console.log('calculateMixedTimeBlockCost - actualEndTime:', actualEndTime.format('HH:mm'));
    
    breakdown.push({ period: periodType, hours: hoursInSlot, rate, cost });
    totalCost += cost;
    
    currentTime = actualEndTime;
    // console.log('calculateMixedTimeBlockCost - updated currentTime:', currentTime.format('HH:mm'));
  }
  
  return { totalCost, breakdown };
};